import { useParticipants, useRoomContext } from '@livekit/components-react';
import { ConnectionQuality } from 'livekit-client';
import { Mic, MicOff, PhoneOff, SignalLow, UserMinus } from 'lucide-react';
import { useState } from 'react';

import { SidePanel } from '@/components/call/SidePanel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { endMeeting, hostAction, MeetApiError } from '@/lib/api';
import { cn, initials } from '@/lib/utils';

/**
 * Participant roster plus the host's moderation actions.
 *
 * The mute and remove calls go through the Meet API rather than the client's
 * own roomAdmin grant, so the server can record who did what and a tampered
 * client cannot act on a room it was not issued a host key for.
 */
export function PeoplePanel({
  code,
  joinKey,
  isHost,
  onClose,
  onEnded,
}: {
  code: string;
  joinKey: string;
  isHost: boolean;
  onClose: () => void;
  onEnded: () => void;
}) {
  const room = useRoomContext();
  const participants = useParticipants();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmEnd, setConfirmEnd] = useState(false);

  const run = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof MeetApiError ? err.message : 'That did not work. Try again.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <SidePanel
      title={`In this call · ${participants.length}`}
      onClose={onClose}
      closeLabel="Close people panel"
      error={error}
      footer={
        isHost ? (
          <>
            <Button
              variant="destructive"
              className="w-full"
              onClick={() => setConfirmEnd(true)}
              disabled={busy !== null}
            >
              <PhoneOff />
              End meeting for everyone
            </Button>

            <Dialog open={confirmEnd} onOpenChange={setConfirmEnd}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>End this interview for everyone?</DialogTitle>
                  <DialogDescription>
                    Everyone is disconnected immediately and the recording is finalised. The link
                    keeps working, so you can both rejoin later.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline">Cancel</Button>
                  </DialogClose>
                  <Button
                    variant="destructive"
                    disabled={busy !== null}
                    onClick={() =>
                      run('end', async () => {
                        await endMeeting(code, joinKey);
                        onEnded();
                      })
                    }
                  >
                    End for everyone
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        ) : undefined
      }
    >
      <ul className="participant-list flex-1 space-y-1.5 overflow-y-auto p-3">
        {participants.map((participant) => {
          const isSelf = participant.identity === room.localParticipant.identity;
          const name = participant.name || participant.identity;
          const micOn = participant.isMicrophoneEnabled;
          const weak = participant.connectionQuality === ConnectionQuality.Poor;

          return (
            <li
              key={participant.identity}
              className="participant-row border-border/60 rounded-xl border bg-white/[0.03] p-3"
            >
              <div className="flex items-center gap-3">
                <Avatar className="size-9 shrink-0 ring-1 ring-white/10">
                  <AvatarFallback>{initials(name)}</AvatarFallback>
                </Avatar>

                <div className="min-w-0 flex-1">
                  <p className="participant-name flex items-center gap-1.5 text-sm font-medium">
                    <span className="truncate">{name}</span>
                    {isSelf && (
                      <Badge variant="outline" className="participant-tag shrink-0 px-1.5 py-0">
                        you
                      </Badge>
                    )}
                  </p>

                  <p className="participant-meta text-muted-foreground mt-0.5 flex items-center gap-2.5 text-xs">
                    <span
                      className={cn(
                        'mic-state inline-flex items-center gap-1',
                        !micOn && 'is-muted text-[var(--warning)]',
                      )}
                    >
                      {micOn ? <Mic className="size-3" /> : <MicOff className="size-3" />}
                      {micOn ? 'mic on' : 'muted'}
                    </span>
                    {weak && (
                      <span className="quality-warn inline-flex items-center gap-1 text-[var(--warning)]">
                        <SignalLow className="size-3" />
                        weak
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {isHost && !isSelf && (
                <div className="participant-actions mt-2.5 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    disabled={busy !== null || !micOn}
                    onClick={() =>
                      run(`mute-${participant.identity}`, () =>
                        hostAction(code, joinKey, {
                          identity: participant.identity,
                          action: 'mute',
                          kind: 'audio',
                        }),
                      )
                    }
                  >
                    <MicOff />
                    Mute
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive flex-1 border-[color-mix(in_oklab,var(--destructive)_35%,transparent)] hover:bg-[color-mix(in_oklab,var(--destructive)_14%,transparent)]"
                    disabled={busy !== null}
                    onClick={() =>
                      run(`remove-${participant.identity}`, () =>
                        hostAction(code, joinKey, {
                          identity: participant.identity,
                          action: 'remove',
                        }),
                      )
                    }
                  >
                    <UserMinus />
                    Remove
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </SidePanel>
  );
}
